import { useEffect, useState, useRef, useCallback } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { hubRows, canContinue, showsWarningSigns } from './summaryFlow'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { readStoredReadingLevel } from '../../utils/readingLevel'
import { ArrowLeft, ChevronRight, RefreshCw, AlertTriangle, List, Pencil, StickyNote } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useI18n } from '../../i18n'
import { useQuestionnaireTranslation } from '../../i18n/questionnaires/useQuestionnaireTranslation'
import { aiAPI } from '../../services/api'
import { OrellaChatBubble, ChatKeyframes } from '../../components/OrellaChatHeader'
import OrellaThinkingBubble from '../../components/OrellaThinkingBubble'
import DeleteCaseModal from '../../components/DeleteCaseModal'
import { isHeldByDoctor, reportDeleteFailure } from '../../utils/caseDelete'
import { lockedSummaryNext } from '../../utils/summaryNext'
import { isDescribeLocked, hasWrittenSummary } from '../../utils/describeLock'
import PatientNoteCard from '../../components/PatientNoteCard'
import { warningSignRows, warningSignCount, keyedFromFlags } from '../../utils/warningSigns'
import { dxHook } from '../../utils/patientWording'
import { summaryUnavailableFor } from '../../utils/summaryUnavailable'
import { hasUnseenSummaryUpdate, markSummaryUpdateSeen } from '../../utils/summaryFreshness' // s111 (TF b78)
import { useAgent } from '../../components/AgentContext'
// v181.8.3: Removed AskOrellaChip from ViewSummary sequence
import orellaLogoWhite from '../../assets/logos/orella_white_logo.png'
import Sources from '../../components/Sources'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// ═══════════════════════════════════════════════════════
// ★★★ s98 (v242.242.0) — THE SUMMARY IS A HUB, NOT A WALK (Dr. Rami, mockup C v3, 2026-10-07).
//   /case/describe/summary     the hub: Orella bubble, Clinical History, three rows, and
//                              "Continue building my case" (→ Detect).
//   /case/describe/conditions  today's DxCard, opened, one per swipe (dots; the swipe hint taps to the next).
//   /case/describe/warnings    today's "Watch for these" card + one 911 line.
//   ONE component, three views (the `view` prop from the route): the summary is loaded once,
//   by the same cache / stream code, and every view reads the same state. The step machine,
//   its auto-open / auto-advance timers and the closing Submit CTA are gone; summaryFlow.js
//   now holds the hub's rules (rows, when Continue may show, the swipe arithmetic) and the
//   clinical one: warning signs render ONLY in the warnings view, reached by a tap.
// ═══════════════════════════════════════════════════════
// ViewSummary v118.2 — Streaming Orella Chat Dialogue
// SSE-powered: sections render as AI generates them
// Same chat flow, same 3-tier diagnosis grouping
// First content visible in ~2-3s instead of 40s
// ═══════════════════════════════════════════════════════

// ── VIEWSUMMARY-LEAN-1 (v242.67.0) · ONE HUE, THREE INTENSITIES ──────────────
// The green / amber / red tier colours are gone. Strength is carried by saturation
// and fill inside the Orella ramp, so the differential reads as one scale rather
// than three colour-coded groups. Keys stay the server's English likelihood
// strings, which is what the payload actually carries regardless of UI language.
const LEVEL = {
  High:     { n: 3, rail: '#15354F', bg: '#15354F', text: '#FFFFFF' },
  Moderate: { n: 2, rail: '#46A8F9', bg: '#46A8F9', text: '#FFFFFF' },
  Low:      { n: 1, rail: '#A7D9FC', bg: '#E9F8FF', text: '#3A8FD6' },
}
// Kept under the old name: the Learn More modal still reads it for its header pill,
// and an alias is cheaper and safer than a second edit to code that already works.
const LIKELIHOOD = LEVEL

// ─── Likelihood ring ───
// Three 54-degree arc segments with 36-degree gaps, 234 total sweep, gap centred at
// the bottom, filled 3 / 2 / 1. SEGMENTED, not one continuous arc, and that is the
// whole point: a continuous arc repeated down a column of five reads as five loading
// spinners, which is the wrong association on a clinical screen. The round caps eat
// roughly 10 degrees off each end, which is why the gap is set as wide as 36.
function LikelihoodRing({ level, color, size = 24 }) {
  const R = 9
  const C = 2 * Math.PI * R
  const seg = C * 0.15
  const pitch = C * 0.25
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ transform: 'rotate(-207deg)' }} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <circle
          key={i} cx="12" cy="12" r={R} fill="none" strokeWidth="3.2" strokeLinecap="round"
          stroke={i < level ? color : '#E8F1F8'}
          strokeDasharray={`${seg} ${C - seg}`}
          strokeDashoffset={-i * pitch}
        />
      ))}
    </svg>
  )
}

// ─── Condition Card ───
// VIEWSUMMARY-LEAN-1: the card is a HOOK, not a summary. Rank, name, likelihood ring
// and one short line. Everything deeper is fetched only if the patient opens it.
// `patientDescription` IS that line now — the server redefined the field rather than
// adding a new `hook`, so iOS Build 26 (frozen on TestFlight reading exactly this
// key) keeps rendering instead of going blank.
function DxCard({ item, rank, open, onToggle, onLearnMore, why, onRetry, t }) {
  const raw = item.likelihood || ''
  const key = raw === 'High' || raw === t('viewSummary.high') ? 'High'
    : raw === 'Moderate' || raw === t('viewSummary.moderate') ? 'Moderate'
    : raw === 'Low' || raw === t('viewSummary.low') ? 'Low' : 'Low'
  const lv = LEVEL[key]
  // R25 / PROD-139 (patient P1): patient wording only (utils/patientWording.js): the item's
  // patientDescription, or a LEGACY conditionsToDiscuss item's description; never another field.
  const hook = dxHook(item)
  // isinstance-of-list is a claim about the container, not its contents — the server
  // has emitted bare strings here before, so normalise per element rather than trust.
  const factors = (Array.isArray(item.supportingFactors) ? item.supportingFactors : [])
    .map(f => (typeof f === 'string' ? f : (f && (f.text || f.factor)) || ''))
    .filter(f => f && f.trim())

  return (
    <div
      className="relative bg-white rounded-2xl overflow-hidden transition-all"
      style={{
        border: open ? '1px solid #A7D9FC' : '1px solid #E8F1F8',
        boxShadow: open ? '0 10px 26px rgba(21,53,79,0.09)' : 'none',
      }}
    >
      <div className="absolute left-0 top-0 bottom-0" style={{ width: '4px', background: lv.rail }} />
      <button onClick={onToggle} disabled={!onToggle} className="w-full text-left flex items-start gap-3 pl-[17px] pr-3.5 py-3.5 active:opacity-90">
        <span
          className="flex-shrink-0 w-[21px] h-[21px] rounded-[7px] flex items-center justify-center text-[11px] font-bold mt-[1px]"
          style={{ background: lv.bg, color: lv.text, border: key === 'Low' ? '1px solid #A7D9FC' : 'none' }}
        >{rank}</span>
        <span className="flex-1 min-w-0">
          <span className="block text-[14.5px] font-bold text-[#15354F] leading-tight">{item.diagnosis || item.condition}</span>
          {hook ? <span className="block text-[12.5px] text-[#5A7185] leading-snug mt-[3px]">{hook}</span> : null}
        </span>
        <span className="flex-shrink-0 flex flex-col items-end gap-1">
          <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: key === 'Low' ? '#93A5B4' : lv.rail }}>
            {t('viewSummary.' + key.toLowerCase())}
          </span>
          <LikelihoodRing level={lv.n} color={lv.rail} />
        </span>
        {onToggle && (
          <ChevronRight size={17} className="flex-shrink-0 self-center text-[#7FA3BF] transition-transform" style={{ transform: open ? 'rotate(90deg)' : 'none' }} aria-hidden="true" />
        )}
      </button>

      {open && (
        <div className="pl-[17px] pr-3.5 pb-3.5 pt-3 border-t border-[#F1F5F8]" style={{ animation: 'fadeUp 0.25s ease' }}>
          {factors.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2.5">
              {factors.map((f, i) => (
                <span key={i} className="text-[10.5px] font-semibold text-[#3A8FD6] bg-[#E9F8FF] border border-[#A7D9FC] px-2 py-[3px] rounded-lg">{f}</span>
              ))}
            </div>
          )}
          <p className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-[#93A5B4] mb-1.5">
            {t('viewSummary.whyThisMatchesYou', 'Why this matches you')}
          </p>
          {why && why.status === 'ready' && (
            <p className="text-[13px] text-[#5A7185] leading-relaxed mb-3">{why.text}</p>
          )}
          {(!why || why.status === 'loading') && (
            <div className="mb-3" aria-hidden="true">
              {['96%', '99%', '62%'].map((w, i) => (
                <div key={i} className="shim" style={{ width: w, height: '9px', marginBottom: '7px' }} />
              ))}
            </div>
          )}
          {why && why.status === 'error' && (
            // Deliberately NO template. A fabricated paragraph here would be
            // indistinguishable from a generated one, which is precisely how a
            // transport failure gets read as a design failure. Fail visible, offer
            // a retry, and never write clinical-looking prose we did not generate.
            <div className="mb-3">
              <p className="text-[12.5px] text-[#93A5B4] leading-relaxed mb-1.5">
                {t('viewSummary.whyUnavailable', 'I could not load that just now.')}
              </p>
              <button onClick={onRetry} className="text-[11.5px] font-bold text-[#46A8F9] uppercase tracking-wider">
                {t('viewSummary.tryAgain', 'Try again')}
              </button>
            </div>
          )}
          <button
            onClick={onLearnMore}
            className="w-full py-2.5 rounded-xl bg-[#15354F] text-white text-[12.5px] font-bold flex items-center justify-center gap-1.5 active:opacity-90"
          >
            {t('viewSummary.learnMoreAboutThis', 'Learn more about this condition')}
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════
export default function ViewSummary({ view = 'hub' }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentCase, updateCase, updateAndSaveCase, deleteCase } = useCaseStore()
  const { t, language } = useI18n()
  const { tfa } = useQuestionnaireTranslation()
  const scrollRef = useRef(null)
  const streamRef = useRef(null)

  // Streaming data — fills progressively
  const [narrative, setNarrative] = useState('')
  const [dxList, setDxList] = useState([])
  const [exams, setExams] = useState([])
  const [examDesc, setExamDesc] = useState([])
  const [questions, setQuestions] = useState([])
  const [redFlags, setRedFlags] = useState([])
  const [redFlagDesc, setRedFlagDesc] = useState([])

  // ── SUMMARY-READING-LEVEL-1 · PARKED (TF b44 #6 and #7) ───────────────────
  // ★★★ THE CONTROL IS REMOVED FROM THIS SCREEN AND THE REWRITE PATH WITH IT.
  //     A summary rewrite takes ~38 s against a 30,000 ms API Gateway timeout that is
  //     NOT raisable (D182/D235/D243), so the call could only ever time out and land
  //     the patient on "Couldn't rewrite this one right now" — reported twice, as
  //     "takes a long time" and as "crashes when run the second time". Neither was a
  //     crash; both were the same deterministic timeout. A control whose success path
  //     is unreachable is worse than no control, and this screen is the patient's
  //     pre-visit read — the wrong place to spend their attention on a dead toggle.
  //
  // ★★ THE LEVEL ITSELF STAYS, READ-ONLY. It is shared with the Diagnose review
  //    through utils/readingLevel.js and is still passed to getConditionWhy, so a
  //    patient who chose plainer language there still gets it in the per-condition
  //    explanations here. Nothing about the DEFAULT rendering changes: Standard has
  //    always been the original text, and that is what this screen now always shows.
  //
  // ★ RE-ADDING IS THE MOUNT PLUS applySummaryLevel/chooseSummaryLevel, both removed
  //   in this commit — but do NOT re-add them against a synchronous endpoint. The fix
  //   is pre-generation at finalize or an async job; the timeout is not negotiable.
  //   aiAPI.summaryReadingLevel is now unreferenced in the app; the endpoint remains.
  const level = readStoredReadingLevel()
  const [fullResult, setFullResult] = useState(null)
  // v187.4: Track when red-flags section is done streaming so closing bubble + submit
  // can appear immediately — don't wait for the full stream (EXAMS/QUESTIONS come after).
  const [redFlagsDone, setRedFlagsDone] = useState(false)
  // ★ D469 (Dr. Rami 2026-10-10: "the summary take a bit to generate"). The share hub's "AI Summary"
  //   step is the clinician hand-off, one Sonnet call measured at 17.6 s on his case, and it only
  //   started when the patient opened the hub. It is started HERE, once, the moment the summary is
  //   complete and persisted; /ai/handoff-summary caches it on the case, so the hub's own call
  //   returns the cached one. Fire-and-forget: a failure leaves the hub to generate it as before.
  const handoffWarmRef = useRef(false)
  const prewarmHandoff = () => {
    if (handoffWarmRef.current || !currentCase?.id) return
    handoffWarmRef.current = true
    aiAPI.generateHandoffSummary(currentCase.id).catch(() => {})
  }

  const isCompleted = currentCase?.status === 'completed'

  // State
  const [loading, setLoading] = useState(true)
  // ★★★ v242.157.0 — VIEWSUMMARY-SILENTFAIL-1. There was no way to say "this
  //   failed". The catch below substituted a generic placeholder narrative and
  //   cleared loading, so a total failure rendered AS IF IT WERE A SUMMARY; and
  //   because the skeleton branch is gated on `fullResult` rather than on
  //   `loading`, the "Analyzing your case" bubble then rendered FOREVER. A
  //   spinner looks like patience (§1dk(e)) — the patient waits on a screen that
  //   will never resolve, with Regenerate gated off behind the null fullResult.
  const [genFailed, setGenFailed] = useState(false)
  const [aiGen, setAiGen] = useState(false)

  // UI
  const [modal, setModal] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  // ★ s104 (TestFlight b70, Dr. Rami: "it asks me to continue building case and when I click it changes to
  //   continue and I have to click again"). handleSubmit writes describeComplete to the store FIRST and
  //   then awaits the API save, so the footer flipped to the locked "Continue" mid-submit and looked
  //   like a second step. A summary is locked only when it was submitted BEFORE this tap: while our own
  //   submit runs, the footer keeps "Continue building my case" with its spinner, and one tap navigates.
  //   scripts/check-locked-summary-next.mjs SN-3 holds it.
  const isLocked = currentCase?.describeComplete === true && !submitting
  const [showDelete, setShowDelete] = useState(false)
  const [showNote, setShowNote] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // FAB dialogue for Learn More modal
  const { setFabDialogue, openAgent, suppressFab } = useAgent()
  // v219.4: learnMore is no longer in the bulk summary — fetch on demand when
  // the modal opens for a condition that doesn't have it yet. Modal opens
  // instantly with description/whyRelevant; learnMore streams in under a loader.
  const [learnMoreLoading, setLearnMoreLoading] = useState(false)
  // ★ v242.235.0 (session 96) — the on-demand texts of a FINAL summary ("why this may apply to
  //   you", "learn more") are saved into it once: in the store now, on the server field by field
  //   (aiAPI.enrichSummary fills an EMPTY field and never replaces one). Before a summary is
  //   final nothing is saved, because that summary may still be rebuilt.
  const saveSummaryField = useCallback((kind, name, fields) => {
    const cc = useCaseStore.getState().currentCase
    // s98 · D460: saved once the summary is written (the server keeps it with the summary it
    //   belongs to; a rebuild after an edit starts clean).
    if (!cc?.id || !name || !hasWrittenSummary(cc)) return
    const ps = cc.patientSummary || cc.patient_summary
    if (!ps || typeof ps !== 'object') return
    // Red flags are stored {finding, relevance, urgency} only (R25), so their on-demand texts
    // live beside the list in ps.redFlagDetails[finding] — the same place the server writes them.
    if (kind === 'redFlag') {
      const nm = String(name).trim()
      const details = (ps.redFlagDetails && typeof ps.redFlagDetails === 'object') ? ps.redFlagDetails : {}
      const cur = { ...(details[nm] || {}) }
      const freshRf = {}
      for (const [f, v] of Object.entries(fields)) {
        if (!cur[f] && v && (typeof v === 'string' || (Array.isArray(v) && v.length))) { cur[f] = v; freshRf[f] = v }
      }
      if (!Object.keys(freshRf).length) return
      const nextPsRf = { ...ps, redFlagDetails: { ...details, [nm]: cur } }
      updateCase({ patientSummary: nextPsRf, patient_summary: nextPsRf })
      for (const [f, v] of Object.entries(freshRf)) aiAPI.enrichSummary(cc.id, { kind, name: nm, field: f, value: v })
      return
    }
    const key = ps.differentialDx ? 'differentialDx' : 'conditionsToDiscuss'
    const entryName = (e) => (kind === 'dx' ? (e?.diagnosis || e?.condition || '') : (e?.finding || '')).trim()
    const list = Array.isArray(ps[key]) ? ps[key] : []
    const idx = list.findIndex((e) => entryName(e) === String(name).trim())
    if (idx < 0) return
    const entry = { ...list[idx] }
    const fresh = {}
    for (const [f, v] of Object.entries(fields)) {
      if (!entry[f] && v && (typeof v === 'string' || (Array.isArray(v) && v.length))) { entry[f] = v; fresh[f] = v }
    }
    if (!Object.keys(fresh).length) return
    const nextList = list.slice(); nextList[idx] = entry
    const nextPs = { ...ps, [key]: nextList }
    updateCase({ patientSummary: nextPs, patient_summary: nextPs })
    for (const [f, v] of Object.entries(fresh)) {
      aiAPI.enrichSummary(cc.id, { kind, name: String(name).trim(), field: f, value: v })
    }
  }, [updateCase])
  const handleOpenModal = useCallback((d) => {
    setModal(d)
    suppressFab?.(true)
    // v219.5.7: Red-flag Learn More — the bulk summary now emits only a one-
    // sentence description per flag; the deeper explainer is fetched on demand
    // here, reusing the SAME modal (the flag is passed shaped with `condition`
    // = finding so the modal header/sections render unchanged). Routed by the
    // _isRedFlag marker so DX cards keep using getConditionDetail.
    if (d && d._isRedFlag && !d.learnMore && d.finding) {
      // A final summary may already carry this flag's explainer (saved once, redFlagDetails).
      const ccRf = useCaseStore.getState().currentCase
      const psRf = ccRf?.patientSummary || ccRf?.patient_summary
      const storedRf = psRf && typeof psRf === 'object' && psRf.redFlagDetails ? psRf.redFlagDetails[String(d.finding).trim()] : null
      if (storedRf?.learnMore) {
        setModal({ ...d, learnMore: storedRf.learnMore, sources: storedRf.sources || [] })
        return
      }
      setLearnMoreLoading(true)
      aiAPI.getRedFlagDetail(
        d.finding,
        narrative || '',
        d.relevance || d.whyRelevant || '',
        d.urgency || '',
        language
      ).then((res) => {
        if (res?.detail) {
          setModal((cur) => (cur && cur._isRedFlag && cur.finding === d.finding
            ? { ...cur, learnMore: res.detail, sources: res.sources || [] }
            : cur))
          saveSummaryField('redFlag', d.finding, { learnMore: res.detail, sources: res.sources || [] })
        }
      }).finally(() => setLearnMoreLoading(false))
      return
    }
    if (d && !d.learnMore && (d.diagnosis || d.condition)) {
      setLearnMoreLoading(true)
      aiAPI.getConditionDetail(
        d.diagnosis || d.condition,
        narrative || '',
        d.whyRelevant || '',
        language,
        d.icd10 || d.code || ''
      ).then((res) => {
        if (res?.learnMore) {
          setModal((cur) => (cur && (cur.diagnosis || cur.condition) === (d.diagnosis || d.condition)
            ? { ...cur, learnMore: res.learnMore, sources: res.sources || [] }
            : cur))
          saveSummaryField('dx', d.diagnosis || d.condition, { learnMore: res.learnMore, sources: res.sources || [] })
        }
      }).finally(() => setLearnMoreLoading(false))
    }
  }, [suppressFab, narrative, language, saveSummaryField])
  // ── VIEWSUMMARY-LEAN-1: expand a card in place ───────────────────────────
  // The bulk differential no longer carries `whyRelevant` — it is fetched per
  // condition, once, when the patient actually opens one. Keyed by index AND name
  // so two entries with the same diagnosis cannot share a slot.
  const [openDx, setOpenDx] = useState(null)
  const [whyMap, setWhyMap] = useState({})
  const dxKey = (d, i) => i + ':' + (d.diagnosis || d.condition || '')

  const fetchWhy = useCallback((d, i, force = false) => {
    const k = dxKey(d, i)
    // A summary stored before VIEWSUMMARY-LEAN-1, or one produced by the legacy
    // non-streaming path, may already carry the text. Cache replay hands those back
    // verbatim, so check before spending a call.
    const existing = (d.whyRelevant || '').trim()
    if (existing && !force) {
      setWhyMap(m => ({ ...m, [k]: { status: 'ready', text: existing } }))
      return
    }
    setWhyMap(m => (m[k] && m[k].status === 'ready' && !force ? m : { ...m, [k]: { status: 'loading' } }))
    aiAPI.getConditionWhy(
      d.diagnosis || d.condition || '',
      narrative || '',
      Array.isArray(d.supportingFactors) ? d.supportingFactors : [],
      language,
      level
    ).then((res) => {
      setWhyMap(m => ({
        ...m,
        [k]: res && res.whyRelevant
          ? { status: 'ready', text: res.whyRelevant }
          : { status: 'error' },
      }))
      // ★ v242.235.0: saved into the summary once, so this condition reads the same on every
      //   open (it used to be regenerated each time). The server fills an EMPTY field only.
      if (res && res.whyRelevant) saveSummaryField('dx', d.diagnosis || d.condition || '', { whyRelevant: res.whyRelevant })
    }).catch(() => {
      setWhyMap(m => ({ ...m, [k]: { status: 'error' } }))
    })
  }, [narrative, language, level, saveSummaryField])

  const toggleDx = useCallback((d, i) => {
    const k = dxKey(d, i)
    const wasOpen = openDx === k
    setOpenDx(wasOpen ? null : k)
    if (!wasOpen) fetchWhy(d, i)
  }, [openDx, fetchWhy])

  const handleCloseModal = useCallback(() => {
    setModal(null)
    setLearnMoreLoading(false)
    suppressFab?.(false)
    setFabDialogue(null)
  }, [setFabDialogue, suppressFab])
  
  const SUMMARY_ROUTE = '/case/describe/summary'
  const goBack = () => {
    if (view !== 'hub') return SUMMARY_ROUTE
    if (isCompleted || location.state?.from === 'caseHome' || isLocked) return '/case/home'
    return '/case/describe/review'
  }

  // ─── Actions ───
  // ★ s98: "Continue building my case" commits Describe exactly as the old Submit did and goes
  //   to DETECT (Dr. Rami: Describe → Detect → share). The detect-prep warm-up is still fired
  //   here, now right before the screen that reads it.
  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await updateAndSaveCase({
        describeComplete: true, hasViewedDescribeSummary: true,
        answers: currentCase.answers,
        voiceTextInput: currentCase.voiceTextInput || '',
        voiceRecording: currentCase.voiceRecording || null,
        describeSummary: narrative || '',
      })
      try {
        aiAPI.generateDetectPrep(currentCase.id, language)
      } catch (_) { /* non-blocking by design */ }
      navigate('/case/detect', { replace: true })
    } catch (err) {
      console.error('Submit error:', err)
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    // DELETE-CURRENTCASE-NULL-1 — NAVIGATE FIRST, DELETE SECOND.
    // deleteCase() sets currentCase to null in the store. Awaiting it here re-rendered
    // THIS screen against that null while it was still mounted, and the first thing in
    // render that reads currentCase.<x> without a ?. threw a React error. The delete and
    // the navigation both completed, which is exactly why it looked cosmetic: the app
    // "worked" and threw anyway. Unmounting first removes the whole class, at every one
    // of the six screens that can delete their own case, rather than optional-chaining
    // whichever line happened to fire (RULES §1bv(a)).
    const id = currentCase?.id
    setShowDelete(false)
    navigate('/home', { replace: true })
    // ★ PROD-416: a delete the server refuses leaves the case, and the patient is told.
    if (id) { try { await deleteCase(id) } catch (err) { reportDeleteFailure(t, err) } }
  }

  const handleDraft = async () => {
    if (currentCase?.id) await updateAndSaveCase({ status: 'draft' })
    setShowDelete(false)
    navigate('/cases', { replace: true })
  }

  // ─── Specialty ───
  const specId = currentCase?.specialtyId || currentCase?.specialty?.id
  const faId = currentCase?.focusAreaId || currentCase?.focusArea?.id
  const faName = currentCase?.focusAreaName || currentCase?.focusArea?.name || 'health concern'
  const specName = currentCase?.specialtyName || currentCase?.specialty?.name || 'General'

  // ─── Load from cache ───
  const loadFromCache = useCallback(() => {
    const saved = currentCase?.patientSummary || currentCase?.patient_summary
    if (!saved) return false
    const sLang = saved?.language || 'en'
    // ★★ v242.235.0 (session 96) — A FINAL SUMMARY IS SHOWN AS WRITTEN. Dr. Rami's ruling: the
    //   summary is written once and stays in its original language. Locked + written is final
    //   (utils/describeLock.js; the server's _patient_summary_final), so neither a language
    //   switch nor the "no likelihood, re-stream it" rule below may send it back to the generator.
    const isFinal = isDescribeLocked(currentCase) && hasWrittenSummary(currentCase)
    if (!isFinal && (!(saved.isUnifiedPVS || saved.conditionsToDiscuss?.length > 0) || sLang !== language)) return false

    // v199.3: Prefer differentialDx (has likelihood) over conditionsToDiscuss (legacy, no likelihood)
    const allDx = saved.differentialDx || saved.conditionsToDiscuss || []
    
    // v199.3: If conditions exist but none have likelihood, reject cache and
    // force streaming path which always returns proper likelihood grouping.
    // This prevents the "Info" badge fallback and ensures proper High/Moderate/Low tiers.
    const hasLikelihood = allDx.some(d => d.likelihood)
    if (!isFinal && allDx.length > 0 && !hasLikelihood && !saved.isUnifiedPVS) {
      console.log('[ViewSummary] Cache has conditions but no likelihood data — forcing stream for proper grouping')
      return false
    }

    console.log('[ViewSummary] Using local cache')
    setNarrative(saved.patientNarrative || saved.summary || '')
    setDxList(allDx)
    setExams(saved.examinationEssentials || [])
    setExamDesc(saved.examDescriptions || saved.suggestedExams || [])
    setQuestions(saved.patientQuestions || saved.questionsForDoctor || [])
    setRedFlags(saved.redFlags || [])
    setRedFlagDesc(saved.redFlagDescriptions || [])
    setFullResult(saved)
    setRedFlagsDone(true)
    setAiGen(saved.isAIGenerated !== false)
    setLoading(false)
    return true
  }, [currentCase, language])

  // ─── Streaming load ───
  const loadStream = useCallback((force = false) => {
    setLoading(true)
    setGenFailed(false)
    setNarrative('')
    setDxList([])
    setExams([])
    setExamDesc([])
    setQuestions([])
    setRedFlags([])
    setRedFlagDesc([])
    setFullResult(null)
    setRedFlagsDone(false)

    // ★ s98 · D460: until the answers lock, the hub always asks the server, which replays the
    //   stored summary when the answers are unchanged and rebuilds it once when they changed.
    //   The sub-views read the copy the hub just loaded.
    if (!force && (view !== 'hub' || isDescribeLocked(currentCase)) && loadFromCache()) return

    if (!currentCase?.id) {
      setLoading(false)
      setNarrative(t('describe.seekingCareFor', { condition: faName.toLowerCase() }))
      return
    }

    console.log('[ViewSummary] Starting SSE stream...')
    if (streamRef.current) streamRef.current.abort()

    streamRef.current = aiAPI.streamPatientSummary(currentCase.id, language, force, {
      onNarrative: (text) => {
        console.log('[ViewSummary] Narrative arrived')
        setNarrative(text)
        setAiGen(true)
        setLoading(false)
      },
      onDx: (dx) => {
        console.log('[ViewSummary] DX arrived, likelihood present:', !!dx.likelihood)
        setDxList(prev => [...prev, dx])
      },
      onExams: (examArr) => {
        console.log('[ViewSummary] Exams:', examArr.length)
        setExams(examArr)
        setExamDesc(examArr.map(e => e.description || ''))
        // v187.4: Exams stream AFTER red flags — if we get here, red flags section is done
        setRedFlagsDone(true)
      },
      onQuestions: (qArr) => {
        console.log('[ViewSummary] Questions:', qArr.length)
        setQuestions(qArr)
      },
      onRedFlags: (flags) => {
        console.log('[ViewSummary] Red flags:', flags.length)
        setRedFlags(flags)
        // R25 (patient P1): each streamed sign is ONE object; its advice is keyed to its own
        // finding (utils/warningSigns.js keyedFromFlags), so this first viewing reads exactly what
        // the stored summary reads the second time.
        setRedFlagDesc(keyedFromFlags(flags))
        setRedFlagsDone(true)
      },
      onClinical: () => {},
      onComplete: (result) => {
        console.log('[ViewSummary] Stream complete, Dx:', result.differentialDx?.length)
        setFullResult(result)
        setRedFlagsDone(true)
        updateCase({ patientSummary: result, patient_summary: result })
        // v218.0.6 — enforce the ViewSummary CHECKPOINT invariant.
        //
        // patient_summary (narrative + committed differential) is a system
        // checkpoint: it is expensive, it is the "last hard stop" everything
        // downstream builds on, and it is COMPLETE here at ViewSummary. It MUST
        // persist. The stream generator only writes it server-side if its
        // `event: complete` parse fires; when the client receives the full
        // stream but that capture doesn't land, it lived only in the client
        // store and was never written to the DB — the root cause of the bug.
        //
        // Fix: persist the EXACT streamed result verbatim and idempotently.
        // Never regenerate — a regenerated summary would yield a DIFFERENT
        // differential than the one the patient just reviewed, corrupting the
        // checkpoint. persistSummary writes this result as-is, or no-ops if a
        // valid summary already exists (stream did land). Detect is a SEPARATE
        // bucket (detect_data + MD exams) — detect-prep is non-critical
        // downstream enhancement, kept best-effort and not gated on this.
        const lang = (typeof language === 'string' && language) || 'en'
        aiAPI.persistSummary(currentCase.id, result)
          .then((res) => {
            // ★ v242.235.0: another generator (the server prewarm) wrote first, and the server
            //   keeps ITS copy. Show the kept one now, so the summary the patient reads is the
            //   one they will find tomorrow — never a different one on their next visit.
            const kept = res && res.reason === 'already_present' ? res.summary : null
            if (kept && typeof kept === 'object' && Array.isArray(kept.differentialDx) && kept.differentialDx.length) {
              setNarrative(kept.patientNarrative || kept.summary || '')
              setDxList(kept.differentialDx || kept.conditionsToDiscuss || [])
              setExams(kept.examinationEssentials || [])
              setExamDesc(kept.examDescriptions || kept.suggestedExams || [])
              setQuestions(kept.patientQuestions || kept.questionsForDoctor || [])
              setRedFlags(kept.redFlags || [])
              setRedFlagDesc(kept.redFlagDescriptions || [])
              setFullResult(kept)
              updateCase({ patientSummary: kept, patient_summary: kept })
            }
          })
          .catch(() => { /* non-blocking; persist is idempotent, safe to miss */ })
          .then(() => prewarmHandoff())
      },
      onError: (err) => {
        // v242.203.0 — TEST 1.14. This used to be `console.error('[ViewSummary] SSE error:', err)`
        // over an object that carried ONE field, `{ error: err.message }`, and `.message` is
        // empty for a cross-origin rejection — which is what an API Gateway kill looks like
        // from a browser. Measured from Alex's console 2026-09-21: the line printed nothing
        // after the colon, twice out of two. The reason is now guaranteed non-empty by
        // streamPatientSummary (message -> name -> phase), and the phase is what says WHERE.
        //
        // ★ THE PATIENT SURFACE IS DELIBERATELY UNCHANGED. They came for the summary, the
        //   fallback still delivers it, and a transport phase is not something to put in
        //   front of someone waiting on a clinical document. A real dead end already has a
        //   patient-facing state — setGenFailed below — and it says the analysis failed in
        //   words that exist in every locale. Two audiences, two surfaces, no new strings.
        console.error(
          `[ViewSummary] SSE error name=${err?.name} status=${err?.status} ` +
          '— falling back to the non-streaming generator (api.js logs the phase and the counters).')
        if (!narrative) {
          console.log('[ViewSummary] Falling back to non-streaming')
          aiAPI.generatePatientSummary(currentCase.id, force, language).then(result => {
            if (result && (result.summary || result.patientNarrative)) {
              setNarrative(result.patientNarrative || result.summary || '')
              setDxList(result.differentialDx || result.conditionsToDiscuss || [])
              setExams(result.examinationEssentials || [])
              setExamDesc(result.examDescriptions || result.suggestedExams || [])
              setQuestions(result.patientQuestions || result.questionsForDoctor || [])
              setRedFlags(result.redFlags || [])
              setRedFlagDesc(result.redFlagDescriptions || [])
              setFullResult(result)
              // ★ The non-streaming fallback is a COMPLETION path, so it owes the
              // same flag the four other completion paths set (loadFromCache,
              // onExams, onRedFlags, onComplete). Without it the closing
              // "Share with Doctor" bubble is gated off by `if (!redFlagsDone)`
              // and the flow dead-ends at red flags — which is precisely what a
              // device reported on 2026-08-01, and why backing out and returning
              // "fixed" it (loadFromCache sets it).
              setRedFlagsDone(true)
              setAiGen(result.isAIGenerated !== false)
              updateCase({ patientSummary: result, patient_summary: result })
            } else {
              // A 200 that carries no summary is the same failure as a throw and
              // used to be quieter than one: it cleared `loading` with nothing on
              // screen and no error.
              setGenFailed(true)
            }
            setLoading(false)
          }).catch(() => {
            // ★★★ v242.157.0 — THIS USED TO WRITE A PLACEHOLDER NARRATIVE HERE.
            //   `describe.seekingCareFor` is a real sentence about the patient's
            //   focus area, so a dead generation rendered as a short summary and
            //   the patient had no way to know the analysis never ran. Say it
            //   failed and offer the retry instead — the state below is what the
            //   Regenerate button could never reach, because it is gated on
            //   `fullResult` and `fullResult` is null in exactly this case.
            setGenFailed(true)
            setLoading(false)
          })
        }
      },
    })
  }, [currentCase?.id, language, faName, loadFromCache])

  useEffect(() => {
    window.scrollTo(0, 0)
    // v160.3: Suppress floating FAB — this page has inline AskOrella chips
    suppressFab?.(true)
    if (currentCase && !loaded) {
      setLoaded(true)
      // ★ s98: the conditions and warnings views only ever READ the summary the hub produced.
      //   They never start a generation of their own (a second stream could write a different
      //   differential than the one the patient just read); with nothing cached they go back.
      if (view !== 'hub') {
        if (!loadFromCache()) navigate('/case/describe/summary', { replace: true })
      } else {
        loadStream()
      }
    }
    return () => { suppressFab?.(false); if (streamRef.current) streamRef.current.abort() }
  }, [])

  // ★ s111 (TF b78): opening the refreshed list is what "seen" means; the Describe step's and the
  //   hub row's "Updated" pill go once the patient has looked at it (utils/summaryFreshness).
  //   Above the redirect: a hook after an early return would change the hook count.
  const refreshedAt = fullResult && fullResult.refreshedWithRecords && fullResult.refreshedWithRecords.at
  useEffect(() => {
    if (view === 'conditions' && refreshedAt && currentCase) markSummaryUpdateSeen(currentCase)
  }, [view, refreshedAt, currentCase])

  // DELETE-CURRENTCASE-NULL-1 — DECLARATIVE REDIRECT, NOT navigate() IN RENDER.
  // Calling navigate() here updates the router WHILE this component is rendering, and
  // React logs exactly that: "Cannot update a component (BrowserRouter) while rendering
  // a different component". That is the React error a patient saw when they deleted a
  // case mid-describe — deleteCase() nulls currentCase, this screen re-rendered on the
  // null and took this arm. The delete and the redirect both completed, which is why it
  // read as cosmetic. <Navigate> is the supported way to say the same thing.
  if (!currentCase) return <Navigate to="/home" replace />


  // ─── Extract data for rendering ───
  const allDx = dxList
  // v199.3: AI always returns English likelihood strings ("High", "Moderate", "Low")
  // regardless of UI language. Match both English and translated values for safety.
  const hasLH = allDx.some(d => d.likelihood)
  // VIEWSUMMARY-LEAN-1: ONE ranked list, not three tier buckets. Sorted strongest
  // first and STABLY — the server already ranks within a likelihood band and that
  // order is clinical, so it has to survive the sort.
  const lvlOf = (d) => {
    const r = d.likelihood || ''
    if (r === 'High' || r === t('viewSummary.high')) return 3
    if (r === 'Moderate' || r === t('viewSummary.moderate')) return 2
    if (r === 'Low' || r === t('viewSummary.low')) return 1
    return 0
  }
  const rankedDx = allDx
    .map((d, i) => ({ d, i, l: lvlOf(d) }))
    .sort((a, b) => (b.l - a.l) || (a.i - b.i))
    .map(x => x.d)

  const hasExams = exams.length > 0 || examDesc.length > 0
  // v162: hasQ removed — questions section replaced by adaptive screen
  // ★★ R22 / PROD-165 + D451 (patient P1): a summary built without the AI shows the D451 note and
  //   NO warning signs (rows stored before orella-api D5b still carry the four generic ones). The
  //   case's pre-visit summary counts only when the flags on screen are its flags, and nothing
  //   counts while the stream is still running (fullResult null). utils/summaryUnavailable.js
  //   owns the rule (review r1 B1).
  const summaryUnavailable = summaryUnavailableFor(fullResult, currentCase?.preVisitSummary || currentCase?.pre_visit_summary, redFlags)
  // D469: a reassurance is not a warning sign (utils/warningSigns isReassurance), so the count
  //   is the rows that render, not the raw list.
  const signCount = warningSignCount(redFlags, redFlagDesc)
  const hasFlags = redFlags.length > 0 && !summaryUnavailable

  // ─── The hub (s98) ───
  // SD / SF keep their names and their derivation: SF is the warning-signs "step", -1 when the
  // case has none (R22 / D451 included through hasFlags). They no longer pace anything.
  let n = 3
  const SD = rankedDx.length > 0 && !summaryUnavailable ? n++ : -1
  const SF = hasFlags ? n++ : -1
  const describeLocked = isDescribeLocked(currentCase) && hasWrittenSummary(currentCase)
  const rows = hubRows({ summaryUnavailable, dxCount: SD !== -1 ? rankedDx.length : 0, flagCount: SF !== -1 ? signCount : 0, describeLocked })
  const continueReady = canContinue({ loading, genFailed, fullResult: !!fullResult, dxCount: allDx.length, redFlagsDone })

  // ★ s107 (Dr. Rami 2026-10-16): the conditions are ONE ranked list (the carousel is gone), on the first
  //   differential and on every refresh. A row opens in place (toggleDx) and fetches its own "why" then.
  const recordsRefresh = fullResult && fullResult.refreshedWithRecords && fullResult.refreshedWithRecords.at
    ? fullResult.refreshedWithRecords : null
  const recordsRefreshWhen = (() => {
    if (!recordsRefresh) return ''
    const dt = new Date(recordsRefresh.at)
    if (Number.isNaN(dt.getTime())) return ''
    try { return dt.toLocaleString(language || 'en', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch { return dt.toLocaleString() }
  })()

  // s111: see the effect above the redirect.
  const conditionsUpdated = hasUnseenSummaryUpdate(currentCase)

  const HubRow = ({ icon, label, onClick, tone = 'ice', badge = null }) => (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 bg-white rounded-[18px] border border-[#E5EDF3] px-3.5 py-3 text-left active:bg-[#F5FBFF] transition-colors"
    >
      <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: tone === 'gray' ? '#F2F6FA' : '#E9F8FF' }}>{icon}</span>
      <span className="flex-1 min-w-0 text-[14.5px] font-bold text-[#15354F] leading-snug">{label}</span>
      {badge}
      <ChevronRight size={18} className="text-[#A9BACA] flex-shrink-0" />
    </button>
  )

  return (
    <div className="min-h-full bg-[#F8FAFB] flex flex-col">
      <ChatKeyframes />
      <style>{`
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .shim { background: linear-gradient(90deg, #E8F1F8 0%, #D8E6F0 50%, #E8F1F8 100%); background-size: 200% 100%; animation: shimmer 1.5s ease-in-out infinite; border-radius: 6px; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideDown { from { transform:translateY(-100%); } to { transform:translateY(0); } }
        @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes overlayExit { from { transform: translateY(0); } to { transform: translateY(-100%); } }
        @keyframes iconPulse { 0%, 100% { transform: scale(1); opacity: 0.85; } 50% { transform: scale(1.06); opacity: 1; } }
        @keyframes ringExpand { 0%, 100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.25); opacity: 0; } }
        @keyframes dotBounce { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.2); } }
        @keyframes checkPop { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
      `}</style>


      {/* ═══ HEADER ═══ */}
      <div className="px-4 pt-4 pb-5 flex-shrink-0 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #15354F 0%, #1E4A6E 60%, #245A80 100%)', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
        <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full" style={{ background: 'rgba(70,168,249,0.08)' }} />
        <div className="absolute -bottom-5 -left-5 w-20 h-20 rounded-full" style={{ background: 'rgba(167,217,252,0.06)' }} />
        <div className="flex items-center justify-between mb-4 relative z-10">
          <button onClick={() => navigate(goBack())} className="p-1.5 -ml-1.5"><ArrowLeft size={22} className="text-white" /></button>
          <img src={orellaLogoWhite} alt="Orella" className="h-[18px] object-contain" />
        </div>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[1.5px] text-[#A7D9FC]/70 bg-white/10 px-3 py-1 rounded-full border border-white/10 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC]" />
            {t('viewSummary.aiCaseAnalysis')}
          </div>
          <h1 className="text-xl font-bold text-white mb-1">{t('describe.yourCaseSummary') || 'Your Case Summary'}</h1>
          <p className="text-[12px] text-white/35">{specName} › {tfa(faName)}</p>
        </div>
      </div>

      {/* ═══ SCROLL ═══ */}
      <div ref={scrollRef} className="flex-1 overflow-auto">

        {loading && (
          <div className="px-4 pt-4 pb-4">
            {/* v225.27.0: the case-summary load gate now uses the same
                conversational treatment as the Describe extraction loader —
                an Orella "thinking" bubble + a skeleton of the summary that's
                loading — instead of a centered spinner. */}
            <div style={{ animation: 'fadeUp 0.3s ease' }}>
              <OrellaThinkingBubble
                message={t('viewSummary.preparingYourSummary')}
                subMessage={t('viewSummary.preparingYourSummarySub') !== 'viewSummary.preparingYourSummarySub' ? t('viewSummary.preparingYourSummarySub') : 'Organizing everything you shared into a clinical picture.'}
              />
            </div>
            <div className="mt-5" style={{ animation: 'orellaSkelPulse 1.8s ease-in-out infinite' }} aria-hidden="true">
              {/* Clinical history card skeleton */}
              <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8] mb-3">
                <div className="h-[10px] w-[38%] rounded-md mb-3" style={{ background: '#E8F1F8' }} />
                <div className="space-y-2">
                  <div className="h-[9px] w-full rounded-md" style={{ background: '#F1F5F9' }} />
                  <div className="h-[9px] w-[96%] rounded-md" style={{ background: '#F1F5F9' }} />
                  <div className="h-[9px] w-[92%] rounded-md" style={{ background: '#F1F5F9' }} />
                  <div className="h-[9px] w-[98%] rounded-md" style={{ background: '#F1F5F9' }} />
                  <div className="h-[9px] w-[68%] rounded-md" style={{ background: '#F1F5F9' }} />
                </div>
              </div>
              {/* Analysis card skeletons */}
              {[0, 1].map((i) => (
                <div key={i} className="rounded-2xl p-4 mb-2.5" style={{ border: '1px solid #EEF2F6' }}>
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-7 h-7 rounded-lg" style={{ background: '#F1F5F9' }} />
                    <div className="h-[11px] w-[55%] rounded-md" style={{ background: '#F1F5F9' }} />
                  </div>
                  <div className="h-[9px] w-[90%] rounded-md mb-2" style={{ background: '#F4F7FA' }} />
                  <div className="h-[9px] w-[75%] rounded-md" style={{ background: '#F4F7FA' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ s98 · HUB / CONDITIONS / WARNINGS ═══ */}
        {!loading && (
          <div className="pb-4">

            {view === 'hub' && (
              <OrellaChatBubble message={t('viewSummary.organizedEverythingClinical')} animationKey={`vs0-${currentCase?.id}`} instant={isLocked} />
            )}
            {view === 'hub' && (
              <div className="px-4" style={{ animation: 'fadeUp 0.3s ease' }}>
                <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8]">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#46A8F9] mb-2">{t('viewSummary.clinicalHistory')}</p>
                  <p className="text-[14px] text-gray-600 leading-relaxed">{narrative}</p>
                </div>

                {/* Loading/regenerate fallbacks if no DX yet */}
                {/* v225.28.0: inline analysis wait gets the same conversational
                    treatment — Orella "thinking" bubble + a skeleton of the
                    diagnosis cards streaming in. */}
                {allDx.length === 0 && !fullResult && !genFailed && (
                  <div className="mt-4">
                    <OrellaThinkingBubble
                      message={t('viewSummary.analyzingCase') !== 'viewSummary.analyzingCase' ? t('viewSummary.analyzingCase') : 'Analyzing your case'}
                      subMessage={t('viewSummary.analyzingCaseSub') !== 'viewSummary.analyzingCaseSub' ? t('viewSummary.analyzingCaseSub') : 'Reviewing your clinical picture against the possible conditions.'}
                    />
                    <div className="mt-4" style={{ animation: 'orellaSkelPulse 1.8s ease-in-out infinite' }} aria-hidden="true">
                      <div className="h-[11px] w-[45%] rounded-md mb-3" style={{ background: '#EEF2F6' }} />
                      {[0, 1].map((i) => (
                        <div key={i} className="rounded-2xl p-4 mb-2.5" style={{ border: '1px solid #EEF2F6' }}>
                          <div className="flex items-center gap-2 mb-2.5">
                            <div className="w-7 h-7 rounded-lg" style={{ background: '#F1F5F9' }} />
                            <div className="h-[11px] w-[55%] rounded-md" style={{ background: '#F1F5F9' }} />
                          </div>
                          <div className="h-[9px] w-[90%] rounded-md mb-2" style={{ background: '#F4F7FA' }} />
                          <div className="h-[9px] w-[75%] rounded-md" style={{ background: '#F4F7FA' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* ★★★ v242.157.0 — VIEWSUMMARY-SILENTFAIL-1. The state that had no
                    screen. Regenerate below cannot serve it: that button is gated on
                    `fullResult`, which is null precisely when generation failed. */}
                {genFailed && (
                  <div className="mt-4 rounded-2xl p-4" style={{ border: '1px solid #FDE0E0', background: '#FEF6F6' }} role="alert">
                    <div className="text-[15px] font-semibold text-[#B4232A] mb-1">
                      {t('viewSummary.generationFailed')}
                    </div>
                    <div className="text-[13px] text-[#7A4B4B] mb-3">
                      {t('viewSummary.generationFailedSub')}
                    </div>
                    <button
                      onClick={() => loadStream(true)}
                      className="text-xs bg-white px-3 py-1.5 rounded-full text-[#B4232A] flex items-center gap-1.5 font-medium"
                      style={{ border: '1px solid #FDE0E0' }}
                    >
                      <RefreshCw size={12} /> {t('viewSummary.tryAgain')}
                    </button>
                  </div>
                )}
                {/* v242.95.0 — was `step === 0 && !userChoice && ...`. The
                    auto-open effect now sets userChoice as soon as fullResult is
                    true, which would have made this button unreachable in exactly
                    the case it exists for: the analysis finished and found
                    nothing. Gated on the data instead of on the step. */}
                {allDx.length === 0 && fullResult && (
                  <button onClick={() => loadStream(true)} className="mt-3 text-xs bg-[#E9F8FF] px-3 py-1.5 rounded-full text-[#46A8F9] flex items-center gap-1.5 font-medium">
                    <RefreshCw size={12} /> {t('viewSummary.regenerate', 'Regenerate')}
                  </button>
                )}
              </div>
            )}


            {/* ── the three rows (titles only) ── */}
            {view === 'hub' && (fullResult || allDx.length > 0) && (
              <div className="px-4 mt-3 space-y-2.5" style={{ animation: 'fadeUp 0.3s ease' }}>
                {rows.includes('conditions') && (
                  <HubRow icon={<List size={20} className="text-[#46A8F9]" />} label={t('viewSummary.rowConditions')} onClick={() => navigate('/case/describe/conditions')}
                    badge={conditionsUpdated ? (
                      <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-bold" style={{ background: '#F2FAF3', border: '1px solid #BFE6C3', color: '#1F4D25' }}>
                        <RefreshCw size={11} aria-hidden="true" />{t('caseHome.summaryUpdated')}
                      </span>
                    ) : null} />
                )}
                {rows.includes('warnings') && (
                  <HubRow icon={<AlertTriangle size={20} className="text-[#15354F]" />} label={t('viewSummary.rowWarnings')} onClick={() => navigate('/case/describe/warnings')} />
                )}
                {rows.includes('edit') && (
                  <HubRow tone="gray" icon={<Pencil size={18} className="text-[#5A7185]" />} label={t('viewSummary.rowEdit')} onClick={() => navigate('/case/describe/review')} />
                )}
                {rows.includes('note') && (
                  <HubRow tone="gray" icon={<StickyNote size={18} className="text-[#B45309]" />} label={t('viewSummary.rowNote')} onClick={() => setShowNote(v => !v)} />
                )}
              </div>
            )}

            {/* ═══ R22 / PROD-165 + D451: the summary was built without the AI ═══
                The D451 patient note stands where the two rows would; hubRows drops them. */}
            {view === 'hub' && summaryUnavailable && fullResult && (
              <div className="px-4 mt-3" style={{ animation: 'fadeUp 0.3s ease' }}>
                <div className="bg-[#F1F5F8] rounded-xl px-4 py-3">
                  <p className="text-[12.5px] text-[#5A7185] leading-snug">{t('viewSummary.summaryUnavailableNote')}</p>
                </div>
              </div>
            )}

            {/* ★ v242.235.0 — corrections after the summary go to the doctor as a note */}
            {view === 'hub' && describeLocked && showNote && (
              <PatientNoteCard caseId={currentCase?.id} notes={currentCase?.patientNotes || []} />
            )}

            {view === 'hub' && (fullResult || allDx.length > 0) && (
              <p className="px-6 mt-4 text-center text-[11.5px] text-[#93A5B4] leading-snug">{t('viewSummary.forEducationalPurposesOnlyThis')}</p>
            )}

            {/* ═══ CONDITIONS — one ranked list; a row opens in place (s107, was a carousel) ═══ */}
            {view === 'conditions' && SD !== -1 && (
              <div className="pt-4" style={{ animation: 'fadeUp 0.3s ease' }}>
                <h2 className="mx-4 text-[19px] font-extrabold text-[#15354F] leading-tight tracking-[-0.2px]">{t('viewSummary.rowConditions')}</h2>
                <p className="mx-4 mt-1.5 mb-3 text-[12px] text-[#93A5B4] leading-snug">
                  {t('viewSummary.rankedSubhead', 'Ordered by how closely each one matches what you described. Tap one to learn more. For preparation only — your doctor makes the diagnosis.')}
                </p>
                {recordsRefresh && (
                  <div className="mx-4 mb-3 flex items-start gap-2.5 rounded-xl px-3.5 py-3" style={{ background: '#F2FAF3', border: '1px solid #BFE6C3' }}>
                    <RefreshCw size={17} className="flex-shrink-0 mt-[1px]" style={{ color: '#2F7A38' }} aria-hidden="true" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13.5px] font-bold" style={{ color: '#1F4D25' }}>{t('viewSummary.updatedWithRecords')}</span>
                      <span className="block text-[12px] leading-snug mt-[2px]" style={{ color: '#1F4D25' }}>{t('viewSummary.updatedWithRecordsSub', { date: recordsRefreshWhen })}</span>
                    </span>
                  </div>
                )}
                <div className="px-4 space-y-2.5">
                  {rankedDx.map((d, i) => {
                    const k = dxKey(d, i)
                    const w = whyMap[k]
                    return (
                      <DxCard
                        key={k}
                        item={d}
                        rank={i + 1}
                        open={openDx === k}
                        onToggle={() => toggleDx(d, i)}
                        onLearnMore={() => handleOpenModal({ ...d, whyRelevant: (w && w.text) || d.whyRelevant || '' })}
                        why={w}
                        onRetry={() => fetchWhy(d, i, true)}
                        t={t}
                      />
                    )
                  })}
                </div>
              </div>
            )}

            {/* ═══ WARNINGS — the clinical rule: they render ONLY here, behind a tap (summaryFlow.showsWarningSigns) ═══ */}
            {showsWarningSigns(view) && hasFlags && (
              <h2 className="mx-4 pt-4 text-[19px] font-extrabold text-[#15354F] leading-tight tracking-[-0.2px]">{t('viewSummary.rowWarnings')}</h2>
            )}

            {/* ═══ Warning signs — OUTLINED (VIEWSUMMARY-LEAN-1) ═══ */}
            {/* Was a red-bordered card. The content stays on white and the urgency
                comes from the heaviest rule on the screen plus a label tab straddling
                it — red carries urgency for free and the brand ramp has no red, so
                weight and containment carry it instead. */}
            {showsWarningSigns(view) && SF !== -1 && (
              <>
                {hasFlags && (
                  <div className="px-4 mt-6" style={{ animation: 'fadeUp 0.3s ease' }}>
                    <div className="relative bg-white rounded-[18px] pt-1.5 pb-0.5" style={{ border: '2px solid #15354F' }}>
                      <span
                        className="absolute -top-[13px] left-3.5 flex items-center gap-1.5 bg-[#15354F] text-white text-[9.5px] font-extrabold uppercase tracking-[0.1em] px-2.5 py-[5px] rounded-lg"
                        style={{ boxShadow: '0 0 0 3px #F8FAFB' }}
                      >
                        <AlertTriangle size={11} strokeWidth={2.4} />
                        {t('viewSummary.watchForThese', 'Watch for these')}
                      </span>
                      <span className="absolute -top-[8px] right-4 bg-white px-1.5 text-[9.5px] font-extrabold uppercase tracking-[0.09em] text-[#93A5B4]">
                        {signCount} {t('viewSummary.signs', 'signs')}
                      </span>
                      {/* ★★ TEST 3.13 + R24 (patient P1): the plain-language patientTitle keyed
                          to the flag's own finding leads, with the clinical term beside it; the
                          text is the keyed advice for that finding. Nothing is paired by
                          position, and the clinician's `relevance` is never shown here
                          (utils/warningSigns.js owns the rule). */}
                      {warningSignRows(redFlags, redFlagDesc).map((fl, i) => {
                        const hi = fl.urgency === 'high'
                        const canExpand = fl.urgency === 'high' || fl.urgency === 'moderate'
                        const shortDesc = fl.advice
                        const inner = (
                          <>
                            <p className="flex items-start gap-2 text-[13px] font-bold text-[#15354F] mb-[3px] leading-snug">
                              <AlertTriangle size={14} strokeWidth={2.2} className="flex-shrink-0 mt-[1px]" style={{ color: hi ? '#15354F' : '#46A8F9' }} />
                              <span className="flex-1">
                                {fl.patientTitle || fl.finding}
                                {fl.patientTitle && (
                                  <span className="block text-[11px] font-semibold text-[#5A7185] mt-[1px]">{fl.finding}</span>
                                )}
                              </span>
                            </p>
                            <p className="text-[12px] text-[#5A7185] leading-snug pl-[22px]">{shortDesc}</p>
                            {canExpand && (
                              <p className="pl-[22px] mt-1.5 text-[10px] font-extrabold uppercase tracking-[0.09em] text-[#46A8F9] flex items-center justify-end gap-1">
                                {t('viewSummary.learnMore')}<ChevronRight size={13} />
                              </p>
                            )}
                          </>
                        )
                        const cls = 'px-3.5 py-3 ' + (i > 0 ? 'border-t border-[#E8F1F8]' : '')
                        return canExpand ? (
                          <button
                            key={i}
                            className={cls + ' w-full text-left active:bg-[#E9F8FF] transition-colors'}
                            onClick={() => handleOpenModal({
                              _isRedFlag: true,
                              finding: fl.finding,
                              condition: fl.finding,
                              // R25: the modal shows patient wording only; this IS the patient advice
                              patientDescription: shortDesc,
                              relevance: fl.relevance || '',
                              urgency: fl.urgency || '',
                            })}
                          >
                            {inner}
                          </button>
                        ) : (
                          <div key={i} className={cls}>{inner}</div>
                        )
                      })}
                    </div>
                    <div className="bg-[#F1F5F8] rounded-xl px-4 py-2.5 mt-3">
                      <p className="text-[12px] text-[#15354F] font-bold leading-snug">{t('viewSummary.seekCareLine')}</p>
                    </div>
                    <p className="mt-3 text-center text-[11.5px] text-[#93A5B4] leading-snug">{t('viewSummary.forEducationalPurposesOnlyThis')}</p>
                  </div>
                )}
              </>
            )}

          </div>
        )}
      </div>

      {/* ═══ FOOTER ═══
          ★★ s101 (Alex, Build 65: "fix the continue button ... so one would see it even if they are
          on the beginning of the page"). The page has no scroller of its own — `.scroll-wrapper` is
          the app's ONLY scroller (MobileLayout) — so `flex-1 overflow-auto` above never scrolls and
          this footer sat at the END of the content. It is STICKY to the bottom of that scroller,
          lifted by the TabBar's own height (the same expression as MobileLayout's <main> padding),
          so Continue / Back is on screen from the first line of the summary. */}
      <div data-summary-footer className="p-4 pb-6 flex-shrink-0 bg-white border-t border-[#E8F1F8] sticky z-20" style={{ bottom: 'calc(4rem + max(calc(env(safe-area-inset-bottom, 0px) - 14px), 0px))' }}>
        {view === 'hub' && (
          <>
        {/* ★ TEST 3.12 (patient push 3a): a summary that was already submitted offers the way
            forward: the case's current next step (utils/summaryNext.js).
            scripts/check-locked-summary-next.mjs holds it. */}
        {isLocked && (
          <button data-summary-continue onClick={() => navigate(lockedSummaryNext(currentCase).route)} className="w-full py-3.5 rounded-full text-white text-[14px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform" style={{ backgroundColor: '#15354F' }}>
            {t('common.continue')}
            <ChevronRight size={16} />
          </button>
        )}
            {!isLocked && (
              <button
                onClick={handleSubmit}
                disabled={submitting || !continueReady}
                className="w-full py-3.5 rounded-full text-white text-[15px] font-bold flex items-center justify-center gap-2 active:opacity-90 transition-all disabled:opacity-50"
                style={{ backgroundColor: '#15354F' }}
              >
                {submitting && <OrellaSpinner size={18} tone="inherit" />}
                {t('viewSummary.continueBuilding')}
              </button>
            )}
          </>
        )}
        {/* s99 D477 (Build 61 TestFlight): the conditions swipe and the dots move between
            conditions, so the footer is the one Back button, the same as the warnings view. */}
        {(view === 'conditions' || view === 'warnings') && (
          <button onClick={() => navigate(goBack(), { replace: true })} className="w-full py-3.5 rounded-full border-[1.5px] border-[#D8E6F0] bg-white text-[#15354F] text-[14px] font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
            {t('viewSummary.backToSummary')}
          </button>
        )}
      </div>


      <DeleteCaseModal canDelete={!isHeldByDoctor(currentCase)} isOpen={showDelete} onClose={() => setShowDelete(false)} onDelete={handleDelete} onSaveAsDraft={handleDraft} caseName={currentCase?.focusAreaName || currentCase?.focusArea?.name || ''} />

      {/* ═══ LEARN MORE MODAL ═══ */}
      {modal && (
        <OverlayPortal>
        <div className="fixed inset-0 z-[60] bg-[#F8FAFB] flex flex-col" style={{ animation: 'slideDown 0.3s ease-out' }}>
          <div className="px-4 pb-5 flex-shrink-0 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #15354F 0%, #1E4A6E 60%, #245A80 100%)', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
            <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full" style={{ background: 'rgba(70,168,249,0.08)' }} />
            <div className="flex items-center justify-between mb-4 relative z-10">
              <button onClick={handleCloseModal} className="p-1.5 -ml-1.5"><ArrowLeft size={22} className="text-white" /></button>
              <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">{t('viewSummary.learnMore')}</span>
            </div>
            <div className="relative z-10">
              <h2 className="text-[18px] font-bold text-white">{modal.diagnosis || modal.condition}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[11px] text-white/40">{t('viewSummary.understandingThisCondition')}</span>
                  {modal.likelihood && (() => {
                    const lc = LIKELIHOOD[modal.likelihood] || LIKELIHOOD.Low
                    return <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-md" style={{ color: lc.text, background: lc.bg }}>{modal.likelihood}</span>
                  })()}
                </div>
              </div>
          </div>

          <div className="flex-1 overflow-auto p-4 space-y-3">
            {dxHook(modal) && (
              <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8]">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#46A8F9] mb-2">{t('viewSummary.whatIsIt')}</p>
                <p className="text-[14px] text-gray-600 leading-relaxed">{dxHook(modal)}</p>
              </div>
            )}
            {modal.whyRelevant && (
              <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8] border-l-[3px] border-l-[#46A8F9]">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#15354F] mb-2">{t('viewSummary.whyItMayApplyTo')}</p>
                <p className="text-[14px] text-gray-600 leading-relaxed">{modal.whyRelevant}</p>
              </div>
            )}
            {modal.supportingFactors?.length > 0 && (
              <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8]">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">{t('viewSummary.keyIndicatorsFromYourIntake')}</p>
                <div className="space-y-1.5">
                  {modal.supportingFactors.map((f, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-[5px] h-[5px] rounded-full bg-[#A7D9FC] mt-[7px] flex-shrink-0" />
                      <p className="text-[14px] text-gray-500 leading-relaxed">{typeof f === 'string' ? f : JSON.stringify(f)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(modal.learnMore || learnMoreLoading) && (
              <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8]">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">{t('viewSummary.moreInformation')}</p>
                {modal.learnMore ? (
                  <>
                    <p className="text-[14px] text-gray-600 leading-relaxed">{modal.learnMore}</p>
                    <Sources sources={modal.sources} />
                  </>
                ) : (
                  <div className="flex items-center gap-2 py-1">
                    <OrellaSpinner size={16} />
                    <span className="text-[13px] text-gray-400">{t('viewSummary.loadingMore')}</span>
                  </div>
                )}
              </div>
            )}
            <div className="bg-[#F1F5F9] rounded-xl px-4 py-2.5 text-center">
              <p className="text-[10.5px] text-gray-400 italic">{t('viewSummary.forEducationalPurposesOnlyNot')}</p>
            </div>
          </div>

          <div className="p-4 pb-8 flex-shrink-0 bg-white border-t border-[#E8F1F8]">
            <button onClick={handleCloseModal} className="w-full py-3.5 rounded-full text-[14px] font-semibold text-white" style={{ backgroundColor: '#15354F' }}>{t('viewSummary.gotIt')}</button>
          </div>
        </div>
        </OverlayPortal>
      )}
    </div>
  )
}
